import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IReturnVarianceReportDailyAttribution } from '@/interfaces/rvReport.interface'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import { formatCellValue } from '@/utils/tableGridUtils'
import { formatNumber } from '@/utils/utils'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useState } from 'react'

export type IRVSummary = {
  chartData: IReturnVarianceReportDailyAttribution[]
}

export const RVSummaryChart: React.FunctionComponent<IRVSummary> = ({
  chartData,
}) => {
  const layout = useAppSelector((s) => s.verticallySplitLayout?.contentFocus)
  const [chartOptions, setChartOptions] = useState({
    chart: {
      height: 260,
    },
    title: {
      text: null,
    },
    xAxis: {},
    yAxis: [
      {
        title: {
          text: 'Return(%)',
        },
      },
    ],
    legend: {
      align: 'right',
      verticalAlign: 'top',
      layout: 'vertical',
      itemMarginTop: 5,
      itemMarginBottom: 5,
    },
    series: [],
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: {
        keys: ['y', 'custom.value'],
      },
    },
    tooltip: {
      headerFormat: '<p>Date: {point.x}</p><br/>',
      pointFormat:
        '<p>{series.name}: <strong>{point.options.custom.value}</strong></p>',
    },
  })

  useEffect(() => {
    const portFolioData = {
      name: getColumnDetail('port_total_return', AxiomReportType.RV).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const benchMarkData = {
      name: getColumnDetail('bench_total_return', AxiomReportType.RV).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const activeData = {
      name: getColumnDetail('active_total_return', AxiomReportType.RV).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const allocationLowData = {
      name: getColumnDetail('allocation_low', AxiomReportType.RV).header,
      type: 'column',
      data: [] as [number, string][],
    }
    const selectionEffectData = {
      name: getColumnDetail('selection_effect', AxiomReportType.RV).header,
      type: 'column',
      data: [] as [number, string][],
    }
    const activeCurrencyReturnData = {
      name: getColumnDetail('active_currency_return', AxiomReportType.RV)
        .header,
      type: 'column',
      data: [] as [number, string][],
    }
    const categories: string[] = []

    chartData &&
      chartData.length > 0 &&
      chartData?.forEach((data: IReturnVarianceReportDailyAttribution) => {
        portFolioData.data.push([
          Number(formatNumber(data.port_total_return)),
          formatCellValue(
            formatNumber(data.port_total_return),
            getColumnDetail('port_total_return', AxiomReportType.RV).format,
          ),
        ])
        benchMarkData.data.push([
          Number(formatNumber(data.bench_total_return)),
          formatCellValue(
            formatNumber(data.bench_total_return),
            getColumnDetail('bench_total_return', AxiomReportType.RV).format,
          ),
        ])
        activeData.data.push([
          Number(formatNumber(data.active_total_return)),
          formatCellValue(
            formatNumber(data.active_total_return),
            getColumnDetail('active_total_return', AxiomReportType.RV).format,
          ),
        ])
        allocationLowData.data.push([
          Number(formatNumber(data.allocation_low)),
          formatCellValue(
            formatNumber(data.allocation_low),
            getColumnDetail('allocation_low', AxiomReportType.RV).format,
          ),
        ])
        selectionEffectData.data.push([
          Number(formatNumber(data.selection_effect)),
          formatCellValue(
            formatNumber(data.selection_effect),
            getColumnDetail('selection_effect', AxiomReportType.RV).format,
          ),
        ])
        activeCurrencyReturnData.data.push([
          Number(formatNumber(data.active_currency_return)),
          formatCellValue(
            formatNumber(data.active_currency_return),
            getColumnDetail('active_currency_return', AxiomReportType.RV)
              .format,
          ),
        ])
        categories.push(
          formatCellValue(
            data.begin_date,
            getColumnDetail('begin_date', AxiomReportType.RV).format,
          ),
        )
      })

    portFolioData.marker.enabled = portFolioData.data.length < 5
    benchMarkData.marker.enabled = benchMarkData.data.length < 5
    activeData.marker.enabled = activeData.data.length < 5

    const updatedChartOptions: any = { ...chartOptions }
    updatedChartOptions.xAxis = { categories: categories }
    updatedChartOptions.series[0] = portFolioData
    updatedChartOptions.series[1] = benchMarkData
    updatedChartOptions.series[2] = activeData
    updatedChartOptions.series[3] = allocationLowData
    updatedChartOptions.series[4] = selectionEffectData
    updatedChartOptions.series[5] = activeCurrencyReturnData
    updatedChartOptions.series[3].visible = false
    updatedChartOptions.series[4].visible = false
    updatedChartOptions.series[5].visible = false
    setChartOptions(updatedChartOptions)
  }, [chartData])

  useEffect(() => {
    const chartHeight = layout?.isTopExpanded ? 640 : 260
    setChartOptions((prevChartOptions) => ({
      ...prevChartOptions,
      chart: {
        height: chartHeight,
      },
    }))
  }, [layout])

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />
}
